import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, CircularProgress, Box, Grid, TextField, Typography, Autocomplete } from "@mui/material";
import type { Subscription, ServerLicenseDto, CloudSubscriptionDto } from "client/api-v2";
import { ProductType, addToExistingSubscriptionGroup, getSubscriptionGroups } from "client/api/SubscriptionGroupApi";
import { useEntity } from "components/EntityProvider/EntityProvider";
import { SubmittableFormLayout } from "components/form/SubmittableFormLayout";
import {
  getServerLicenseBySubscriptionId,
  getCloudSubscriptionBySubscriptionId,
} from "../services/subscriptionService";

type UpdateSubscriptionGroupInput = {
  existingSubscriptionGroupId: string;
};

/**
 * Add Existing Subscription Group form for Subscriptions.
 * This component determines the platform type (Server or Cloud),
 * fetches the platform entity ID, and adds to an existing subscription group.
 */
export function SubscriptionAddExistingSubscriptionGroup() {
  const { entity: subscription, refresh: refreshSubscription } = useEntity<Subscription>();
  const navigate = useNavigate();
  const [platformEntityId, setPlatformEntityId] = useState<string | null>(null);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // State for search and selection
  const [options, setOptions] = useState<{ id: string; name: string }[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<{ id: string; name: string } | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const isServerPlatform = subscription?.plan?.productPlatform === "OctopusServer";
  const isCloudPlatform = subscription?.plan?.productPlatform === "OctopusCloud";

  useEffect(() => {
    if (!subscription?.id) {
      setFetchLoading(false);
      setFetchError("Subscription not found");
      return;
    }

    setFetchLoading(true);
    setFetchError(null);

    if (isServerPlatform) {
      getServerLicenseBySubscriptionId(subscription.id)
        .then((license: ServerLicenseDto) => {
          setPlatformEntityId(license.id);
        })
        .catch((err) => {
          console.error("Failed to fetch server license:", err);
          setFetchError("Failed to load server license information");
        })
        .finally(() => {
          setFetchLoading(false);
        });
    } else if (isCloudPlatform) {
      getCloudSubscriptionBySubscriptionId(subscription.id)
        .then((cloudSub: CloudSubscriptionDto) => {
          setPlatformEntityId(cloudSub.id);
        })
        .catch((err) => {
          console.error("Failed to fetch cloud subscription:", err);
          setFetchError("Failed to load cloud subscription information");
        })
        .finally(() => {
          setFetchLoading(false);
        });
    } else {
      setFetchLoading(false);
      setFetchError("Unsupported subscription platform type");
    }
  }, [subscription?.id, isServerPlatform, isCloudPlatform]);

  // API call to fetch matching subscription groups
  const fetchSubscriptionGroups = async (search: string) => {
    setSearchLoading(true);
    try {
      const results = await getSubscriptionGroups({ filter: { name: { like: search } } });
      setOptions(results.entities);
    } catch (error) {
      console.error("Error fetching subscription groups:", error);
    } finally {
      setSearchLoading(false);
    }
  };

  const addToSubscriptionGroupAction = async (input: UpdateSubscriptionGroupInput) => {
    if (!platformEntityId) {
      throw new Error("Platform entity ID not available");
    }

    const productType = isServerPlatform ? ProductType.ServerLicense : ProductType.CloudSubscription;
    await addToExistingSubscriptionGroup(platformEntityId, input.existingSubscriptionGroupId, productType);

    if (refreshSubscription) {
      refreshSubscription();
    }
    navigate(-1);
  };

  if (fetchLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (fetchError || !platformEntityId) {
    return <Alert severity="error">{fetchError || "Unable to load subscription information"}</Alert>;
  }

  return (
    <SubmittableFormLayout header="Add to Subscription group" submitAction={addToSubscriptionGroupAction}>
      {(formController) => {
        const { register, formState, setValue, reset } = formController;
        register("existingSubscriptionGroupId");
        return (
          <Grid container spacing={3}>
            <Grid size={12}>
              <Typography variant="body1">
                You are about to assign this subscription to a subscription group. Use the search below to find and
                select the appropriate group.
              </Typography>
            </Grid>
            <Grid size={12}>
              <Autocomplete
                id="existing-subscription-group"
                options={options}
                getOptionLabel={(option) => option.name}
                value={selectedGroup}
                onChange={(_, newValue) => {
                  setSelectedGroup(newValue);
                  if (newValue) {
                    setValue("existingSubscriptionGroupId", newValue?.id, { shouldDirty: true });
                  } else {
                    reset();
                  }
                }}
                inputValue={inputValue}
                onInputChange={(_, newInputValue) => {
                  setInputValue(newInputValue);
                  if (newInputValue.length >= 2) {
                    fetchSubscriptionGroups(newInputValue);
                  }
                }}
                loading={searchLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search for Subscription Group"
                    variant="standard"
                    fullWidth
                    error={!!formState?.errors?.existingSubscriptionGroupId}
                    helperText={formState?.errors?.existingSubscriptionGroupId?.message}
                    slotProps={{
                      ...params.slotProps,

                      input: {
                        ...params.slotProps.input,
                        endAdornment: (
                          <>
                            {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.slotProps.input.endAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        );
      }}
    </SubmittableFormLayout>
  );
}
